import { alpha, useMediaQuery, useTheme } from '@mui/material';
//@ts-ignore
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Drawer,
  Fade,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popper,
  Tooltip,
  Box,
  Divider,
  IconButton,
  Paper,
  Stack,
  Typography,
} from 'src/components/mui';
import { Icon } from '../icon/Icon';

type Props = {
  isOpen: boolean;
  popupLabel: string;
  button: {
    label: string;
    tooltip: string;
    icon: string;
    onClick?: () => void;
  };
};

export const SidebarItem: React.FC<Props & { children?: any }> = ({
  isOpen,
  button,
  popupLabel,
  children,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isItemOpen, setIsItemOpen] = useState(false);
  const buttonRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handlePopper = useCallback(() => {
    if (buttonRef && buttonRef.current) {
      setAnchorEl(buttonRef.current);
      setIsItemOpen((prev) => !prev);

      if (button.onClick) {
        button.onClick();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDrawer = useCallback(() => {
    setIsItemOpen((prev) => !prev);
  }, []);

  useEffect(() => {
    if (isItemOpen) {
      handlePopper();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <>
      <Tooltip title={isOpen ? '' : button.tooltip} enterDelay={1000} placement="left">
        <ListItemButton
          ref={buttonRef}
          onClick={!children ? button?.onClick : isMobile ? handleDrawer : handlePopper}
          sx={{
            minHeight: 40,
            justifyContent: isOpen ? 'initial' : 'center',
            px: 1,
            '&:hover, &.active': {
              backgroundColor: 'rgba(256,256,256, .2)',
            },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 0,
              mr: isOpen ? 2 : 0,
            }}
          >
            <Icon icon={button.icon} />
          </ListItemIcon>
          <ListItemText
            primary={button.label}
            sx={{ opacity: isOpen ? 1 : 0, textAlign: 'left' }}
          />
        </ListItemButton>
      </Tooltip>
      <>
        {children && (
          <>
            {isMobile ? (
              <Drawer
                open={isItemOpen}
                anchor="bottom"
                onClose={handleDrawer}
                sx={{
                  '&>.MuiPaper-root': {
                    background: 'rgba(0, 0, 0, 0.3)',
                    backdropFilter: 'blur(15px)',
                    color: 'white',
                    px: 2,
                    py: 1,
                    borderRadius: '15px 15px 0px 0px',
                  },
                  '&>.MuiBackdrop-root': {
                    backgroundColor: alpha(theme.palette.common.black, 0.3),
                  },
                }}
              >
                <Stack spacing={2}>
                  <Box>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="h6">{popupLabel}</Typography>
                      <IconButton onClick={handleDrawer}>
                        <Icon icon="fa-times" />
                      </IconButton>
                    </Stack>
                    <Divider sx={{ mt: 1 }} />
                  </Box>

                  {children}
                </Stack>
              </Drawer>
            ) : (
              <Popper placement="right" open={isItemOpen} anchorEl={anchorEl} transition>
                {({ TransitionProps }) => (
                  <Fade {...TransitionProps} timeout={350}>
                    <Paper
                      sx={{
                        mr: '10px',
                        mt: '70px',
                        px: 2,
                        py: 1,
                        background: 'rgba(0, 0, 0, 0.3)',
                        backdropFilter: 'blur(15px)',
                        color: 'white',
                        position: 'relative',
                        width: '450px',
                        borderRadius: '15px',
                      }}
                    >
                      <Stack spacing={2}>
                        <Box>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6">{popupLabel}</Typography>
                            <IconButton onClick={handlePopper} sx={{ color: 'white' }}>
                              <Icon icon="fa-times" />
                            </IconButton>
                          </Stack>
                          <Divider sx={{ mt: 1 }} />
                        </Box>

                        {children}
                      </Stack>
                    </Paper>
                  </Fade>
                )}
              </Popper>
            )}
          </>
        )}
      </>
    </>
  );
};
